import { useState, useEffect } from 'react';
import Login     from './Login';
import Dashboard from './Dashboard';
import api       from './api';
import { ThemeProvider } from './ThemeContext';

// ─── Token persistence (sessionStorage — cleared when tab closes) ─────────────
const TOKEN_KEY = 'crew_portal_token';

function loadSession() {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw); // { token, user }
  } catch {
    return null;
  }
}

function saveSession(token, user) {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify({ token, user }));
}

function clearSession() {
  sessionStorage.removeItem(TOKEN_KEY);
}

// ─── "Open in new tab" bootstrap ───────────────────────────────────────────────
// sessionStorage is per-tab and browsers don't reliably clone it into tabs
// opened with rel="noreferrer"/"noopener" (which we want, for security), so a
// tab opened via "#freelancer/123?t=<jwt>" would otherwise hit an empty
// sessionStorage and force a re-login. Instead, the opening tab embeds a short-
// lived token in the URL; here we pick it up once, resolve it to a user via
// GET /auth/me, save a normal session for THIS tab, then scrub the token out
// of the address bar so it doesn't linger in history.
function readHashToken() {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  if (qIdx === -1) return null;
  return new URLSearchParams(hash.slice(qIdx + 1)).get('t');
}

function stripHashToken() {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  if (qIdx === -1) return;
  const clean = hash.slice(0, qIdx);
  window.history.replaceState(null, '', window.location.pathname + window.location.search + clean);
}

export default function App() {
  const [session, setSession] = useState(() => {
    // Restore token synchronously, BEFORE children mount: Dashboard's data-load
    // effect runs before parent effects, so setting it in useEffect caused
    // 401s on refresh and a false "offline mode" banner.
    const s = loadSession();
    if (s?.token) api.setToken(s.token);
    return s;
  });
  // Ініціалізуємо синхронно з наявності токена в URL, щоб уникнути миготіння
  // екрану логіну на першому рендері нової вкладки (ефект нижче ще не встиг спрацювати).
  const [bootstrapping, setBootstrapping] = useState(() => !session && !!readHashToken());

  useEffect(() => {
    const token = readHashToken();
    if (!token) return;
    if (session) { stripHashToken(); return; } // this tab already has a session — just clean the URL

    setBootstrapping(true);
    api.setToken(token);
    api.auth.me()
      .then(res => {
        saveSession(token, res.user);
        setSession({ token, user: res.user });
      })
      .catch(() => { api.clearToken(); })
      .finally(() => { stripHashToken(); setBootstrapping(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Login callback ────────────────────────────────────────────────────────
  const handleLogin = (user, token) => {
    api.setToken(token);
    saveSession(token, user);
    setSession({ token, user });
  };

  // ── Logout callback ───────────────────────────────────────────────────────
  const handleLogout = () => {
    api.clearToken();
    clearSession();
    setSession(null);
  };

  // ── Update user callback (after profile save) ─────────────────────────────
  const handleUpdateUser = (updatedUser) => {
    const newSession = { ...session, user: { ...session.user, ...updatedUser } };
    saveSession(newSession.token, newSession.user);
    setSession(newSession);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  // ThemeProvider обгортає геть усе, включно з екраном логіну — тема має
  // застосовуватись і до входу в акаунт (currentUser=null до логіну, тому
  // на цьому екрані діє лише localStorage/системна тема).
  if (bootstrapping) {
    return (
      <ThemeProvider currentUser={session?.user}>
        <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dim)', fontFamily: 'Arial, sans-serif', fontSize: '13px' }}>
          Loading…
        </div>
      </ThemeProvider>
    );
  }

  if (!session) {
    return (
      <ThemeProvider currentUser={null}>
        <Login onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  // Map DB role → Dashboard role prop ('admin' | 'freelancer')
  // SuperAdmin і Admin бачать повну адмін-панель.
  const dashRole = ['Admin', 'SuperAdmin'].includes(session.user.role) ? 'admin' : 'freelancer';

  return (
    <ThemeProvider currentUser={session.user}>
      <Dashboard
        role={dashRole}
        currentUser={session.user}   // { id, name, email, role, currency, theme }
        onLogout={handleLogout}
        onUserUpdated={handleUpdateUser}
      />
    </ThemeProvider>
  );
}
